import uniqueId from 'lodash/uniqueId';
import { fg } from '@atlassian/jira-feature-gating';
import type { FieldValidationError } from '@atlassian/jira-fetch/src/utils';
import { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { toAccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	ASSIGNEE_TYPE_PROJECT_LEAD,
	INVALID_PATTERN_PROJECT_KEY_ERROR,
	INVALID_URL_PATTERN,
	PROJECT_KEY_CHANGE_WARNING,
	PROJECT_KEY_MIN_LENGTH,
	PROJECT_KEY_PATTERN,
	PROJECT_NAME_MIN_LENGTH,
	PROJECT_TEAM_TYPE_FREE_TEXT_ERROR,
	PROJECT_URL_MAX_LENGTH,
	REQUIRED_PROJECT_KEY_ERROR,
	TOO_LONG_PROJECT_KEY_ERROR,
	TOO_LONG_PROJECT_NAME_ERROR,
	TOO_LONG_PROJECT_URL_ERROR,
	TOO_SHORT_PROJECT_NAME_ERROR,
} from '../../model/constants';
import type { AllProjectDetailsUpdate, ProjectDetails } from '../../services/project/types';
import {
	FETCH_INSTANCE_PROPERTIES_SUCCESS,
	FETCH_PROJECT_DETAILS_SUCCESS,
	type FetchInstancePropertiesSuccessAction,
	type FetchProjectDetailsSuccessAction,
	SAVE_ALL_PROJECT_DETAILS,
	SAVE_ALL_PROJECT_DETAILS_SUCCESS,
	SAVE_PROJECT_DETAILS,
	SAVE_PROJECT_DETAILS_FAIL,
	SAVE_PROJECT_DETAILS_SUCCESS,
	type SaveAllProjectDetailsAction,
	type SaveAllProjectDetailsSuccessAction,
	type SaveProjectDetailsAction,
	type SaveProjectDetailsFailAction,
	type SaveProjectDetailsSuccessAction,
} from '../entities/actions';
import {
	ARCHIVE_PROJECT_SUCCESS,
	ASSIGNEE_TYPE_CHANGE,
	CLOSE_ARCHIVE_PROJECT_MODAL,
	CLOSE_AVATAR_PICKER_DIALOG,
	CLOSE_DELETE_PROJECT_MODAL,
	CLOSE_TRASH_PROJECT_MODAL,
	DELETE_PROJECT,
	DELETE_PROJECT_FAIL,
	DELETE_PROJECT_SUCCESS,
	DISMISS_FLAG,
	FIELD_VALIDATION_FORCED,
	ON_CHOOSE_PREDEFINED_AVATAR,
	OPEN_ARCHIVE_PROJECT_MODAL,
	OPEN_AVATAR_PICKER_DIALOG,
	OPEN_DELETE_PROJECT_MODAL,
	OPEN_TRASH_PROJECT_MODAL,
	PRELOAD_AVATAR_IMAGE_SUCCESS,
	PROJECT_DESCRIPTION_CHANGE,
	PROJECT_KEY_CHANGE,
	PROJECT_NAME_CHANGE,
	PROJECT_TEAM_TYPE_CHANGE,
	PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE,
	PROJECT_URL_CHANGE,
	type ProjectDetailsUIAction,
	SAVE_BUTTON_CLICKED,
	SET_PROJECT_CATEGORY_ID,
	SET_PROJECT_LEAD_ACCOUNT_ID,
	SHOW_FETCH_AVATARS_ERROR_FLAG,
	TRASH_PROJECT_FAIL,
	TRASH_PROJECT_SUCCESS,
	UPLOAD_AVATAR,
	UPLOAD_AVATAR_FAIL,
	UPLOAD_AVATAR_SUCCESS,
	SET_DROP_DOWN_MENU_BUTTON_REF,
	RELEASE_PROJECT_KEY,
	UNDO_RELEASE_HISTORICAL_KEYS_CLICKED,
} from './actions';
import { validationSuccess } from './selectors';
import {
	AVATAR_FETCH_FAIL_FLAG,
	AVATAR_UPLOAD_FAIL_FLAG,
	DELETE_PROJECT_FAIL_FLAG,
	DELETE_PROJECT_SUCCESS_FLAG,
	type Flag,
	type FlagType,
	GENERIC_ERROR_FLAG,
	SUBMIT_SUCCESS_FLAG,
	type UIState,
	type ValidationMessage,
	RELEASE_KEYS_ERROR,
} from './types';

type Action =
	| ProjectDetailsUIAction
	| SaveProjectDetailsAction
	| SaveAllProjectDetailsAction
	| SaveProjectDetailsSuccessAction
	| SaveAllProjectDetailsSuccessAction
	| SaveProjectDetailsFailAction
	| FetchProjectDetailsSuccessAction
	| FetchInstancePropertiesSuccessAction;

export const uiInitialState: UIState = {
	historicalProjectKeys: [],
	releasedProjectKeys: [],
	isDeleteProjectModalOpen: false,
	isDeleting: false,
	isLoading: false,
	isSubmitting: false,
	isAvatarPickerDialogOpen: false,
	isUploadingAvatar: false,
	isAvatarImageLoaded: false,
	validationErrors: validationSuccess,
	flags: [],
	projectName: '',
	projectKey: '',
	projectDescription: '',
	simplified: false,
	projectUrl: '',
	projectLeadAccountId: toAccountId(''),
	avatar: undefined,
	isTrashProjectModalOpen: false,
	isArchiveProjectModalOpen: false,
	forceValidationFor: undefined,
	projectNameMaxLength: undefined,
	projectKeyMaxLength: undefined,
	allowUnassigned: undefined,
	assigneeType: ASSIGNEE_TYPE_PROJECT_LEAD,
	projectTeamType: undefined,
	projectTeamTypeFreeText: undefined,
	dropDownMenuButtonRef: undefined,
};

const makeFlag = (type: FlagType): Flag => ({
	id: uniqueId('project-details-flag-'),
	type,
});

const addFlagIfLastOneIsNotTheSame = (flags: Flag[], newFlag: Flag): Flag[] => {
	const isLastFlagTheSame =
		flags.length === 0 ? false : flags[flags.length - 1].type === newFlag.type;
	return isLastFlagTheSame ? flags : flags.concat(newFlag);
};

/**
 * Validate project name maxlength and minlength. Minlength is hardcoded in the frontend while maxlength is fetched from
 * the API (because it's configurable by Jira admins). If we failed to get maxlength from the API or it's less than our
 * hard coded minlength we won't validate it on the frontend.
 */
const validateProjectName = (projectName: string, state: UIState): ValidationMessage => {
	const { projectNameMaxLength } = state;

	if (
		projectNameMaxLength !== undefined &&
		projectNameMaxLength >= PROJECT_NAME_MIN_LENGTH &&
		projectName.length > projectNameMaxLength
	) {
		return {
			type: 'error',
			key: TOO_LONG_PROJECT_NAME_ERROR,
		};
	}
	if (projectName.length < PROJECT_NAME_MIN_LENGTH) {
		return {
			type: 'error',
			key: TOO_SHORT_PROJECT_NAME_ERROR,
		};
	}

	return undefined;
};

/**
 * Validate project key maxlength, required and pattern. Pattern and minlength are hardcoded in the frontend while
 * maxlength is fetched from the API (because it's configurable by Jira admins). If we failed to get maxlength from the
 * API or it's less than our hard coded minlength we won't validate it on the frontend.
 */
const validateProjectKey = (projectKey: string, state: UIState): ValidationMessage => {
	const { projectKeyMaxLength } = state;

	if (
		projectKeyMaxLength !== undefined &&
		projectKeyMaxLength >= PROJECT_KEY_MIN_LENGTH &&
		projectKey.length > projectKeyMaxLength
	) {
		return {
			type: 'error',
			key: TOO_LONG_PROJECT_KEY_ERROR,
		};
	}
	if (projectKey.length === 0) {
		return {
			type: 'error',
			key: REQUIRED_PROJECT_KEY_ERROR,
		};
	}
	if (!projectKey.match(PROJECT_KEY_PATTERN)) {
		return {
			type: 'error',
			key: INVALID_PATTERN_PROJECT_KEY_ERROR,
		};
	}

	return !state.simplified || fg('hix-4189-releasing-historical-project-keys')
		? {
				type: 'warning',
				key: PROJECT_KEY_CHANGE_WARNING,
			}
		: undefined;
};

const validateProjectUrl = (projectUrl: string): ValidationMessage => {
	if (fg('hix-5318_-_allow_blank_project_url_in_details')) {
		if (projectUrl.length === 0) {
			return undefined;
		}
	}

	if (projectUrl.length > PROJECT_URL_MAX_LENGTH) {
		return {
			type: 'error',
			key: TOO_LONG_PROJECT_URL_ERROR,
		};
	}

	if (!projectUrl.startsWith('https://') && !projectUrl.startsWith('http://')) {
		return {
			type: 'error',
			key: INVALID_URL_PATTERN,
		};
	}

	return undefined;
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (state: UIState = uiInitialState, action: Action): UIState => {
	switch (action.type) {
		case FETCH_PROJECT_DETAILS_SUCCESS: {
			const {
				name,
				key,
				url,
				description,
				simplified,
				avatar,
				projectCategoryId,
				projectLeadAccountId,
				assigneeType,
				projectTeamType,
				projectTeamTypeFreeText,
				historicalProjectKeys,
			} = action.payload;
			return {
				...state,
				projectName: name,
				projectKey: key,
				projectDescription: description,
				simplified,
				projectUrl: url,
				avatar,
				isAvatarImageLoaded: false,
				projectCategoryId,
				assigneeType,
				projectLeadAccountId,
				projectTeamType,
				projectTeamTypeFreeText,
				historicalProjectKeys,
			};
		}
		case FETCH_INSTANCE_PROPERTIES_SUCCESS: {
			const {
				payload: { projectNameMaxLength, projectKeyMaxLength, allowUnassigned },
			} = action;
			return {
				...state,
				projectNameMaxLength,
				projectKeyMaxLength,
				allowUnassigned,
			};
		}
		case SAVE_PROJECT_DETAILS: {
			return {
				...state,
				isSubmitting: true,
			};
		}
		case SAVE_ALL_PROJECT_DETAILS: {
			return {
				...state,
				isSubmitting: true,
			};
		}
		case SAVE_PROJECT_DETAILS_SUCCESS: {
			const details: ProjectDetails = action.payload;
			const historicalProjectKeys = fg('hix-4189-releasing-historical-project-keys')
				? details.historicalProjectKeys.filter((key) => !state.releasedProjectKeys.includes(key))
				: [];
			return {
				...state,
				isSubmitting: false,
				flags: addFlagIfLastOneIsNotTheSame(state.flags, makeFlag(SUBMIT_SUCCESS_FLAG)),
				projectName: details.name,
				projectUrl: details.url,
				projectKey: details.key,
				projectDescription: details.description,
				simplified: details.simplified,
				avatar: details.avatar,
				projectTeamType: details.projectTeamType,
				projectTeamTypeFreeText: details.projectTeamTypeFreeText,
				historicalProjectKeys,
				releasedProjectKeys: [],
			};
		}
		case SAVE_ALL_PROJECT_DETAILS_SUCCESS: {
			const details: AllProjectDetailsUpdate = action.payload;
			const historicalProjectKeys = fg('hix-4189-releasing-historical-project-keys')
				? details.historicalProjectKeys.filter((key) => !state.releasedProjectKeys.includes(key))
				: [];

			return {
				...state,
				isSubmitting: false,
				flags: addFlagIfLastOneIsNotTheSame(state.flags, makeFlag(SUBMIT_SUCCESS_FLAG)),
				projectName: details.name,
				projectKey: details.key,
				projectDescription: details.description,
				simplified: details.simplified,
				projectUrl: details.url,
				avatar: details.avatar,
				projectCategoryId: details.projectCategoryId,
				historicalProjectKeys,
				releasedProjectKeys: [],
			};
		}
		case SAVE_PROJECT_DETAILS_FAIL: {
			const { error } = action;
			const errors = { ...validationSuccess };
			let { flags } = state;
			if (error instanceof ValidationError) {
				error.errors.forEach((fieldValidationError: FieldValidationError) => {
					if (fieldValidationError.field === 'projectName') {
						errors.projectName = {
							type: 'error',
							body: fieldValidationError.error,
						};
					}
					if (fieldValidationError.field === 'projectKey') {
						errors.projectKey = {
							type: 'error',
							body: fieldValidationError.error,
						};
					}
					if (fieldValidationError.field === 'projectTeamTypeFreeTextValue') {
						errors.projectKey = {
							type: 'error',
							body: fieldValidationError.error,
						};
					}
					if (
						fieldValidationError.field === 'releasedProjectKeys' &&
						fg('hix-4189-releasing-historical-project-keys')
					) {
						errors.releaseProjectKeysError = {
							type: 'error',
							body: fieldValidationError.error,
						};
						flags = flags.concat(makeFlag(RELEASE_KEYS_ERROR));
					}
				});
			} else {
				flags = flags.concat(makeFlag(GENERIC_ERROR_FLAG));
			}

			return {
				...state,
				isSubmitting: false,
				validationErrors: errors,
				flags,
			};
		}
		case UPLOAD_AVATAR_SUCCESS: {
			return {
				...state,
				avatar: action.payload,
				isAvatarImageLoaded: false,
				isUploadingAvatar: false,
			};
		}
		case UPLOAD_AVATAR_FAIL: {
			return {
				...state,
				flags: state.flags.concat(makeFlag(AVATAR_UPLOAD_FAIL_FLAG)),
				isUploadingAvatar: false,
			};
		}
		case SHOW_FETCH_AVATARS_ERROR_FLAG: {
			return {
				...state,
				flags: state.flags.concat(makeFlag(AVATAR_FETCH_FAIL_FLAG)),
			};
		}
		case DISMISS_FLAG: {
			const { payload } = action;
			const flagsAfterAction = state.flags.filter((flag) => flag.id !== payload.id);
			return {
				...state,
				flags: flagsAfterAction,
			};
		}
		case OPEN_AVATAR_PICKER_DIALOG: {
			return {
				...state,
				isAvatarPickerDialogOpen: true,
			};
		}
		case CLOSE_AVATAR_PICKER_DIALOG: {
			return {
				...state,
				isAvatarPickerDialogOpen: false,
			};
		}
		case ON_CHOOSE_PREDEFINED_AVATAR: {
			// The same condition is in project-details/view/form/avatar/avatar-image/view.js
			// if it's false, the component eventually fires PRELOAD_AVATAR_IMAGE_SUCCESS which sets the flag back to true
			// but if user selects the same avatar twice, the preload was never fired, isAvatarImageLoaded never set back to true
			// and UI froze in loading state and required reload.
			// MC-577
			const isLoaded = action.payload === state.avatar;
			return {
				...state,
				avatar: action.payload,
				isAvatarPickerDialogOpen: false,
				isAvatarImageLoaded: isLoaded,
			};
		}
		case PROJECT_NAME_CHANGE: {
			const newState = {
				...state,
				projectName: action.payload,
			};

			const projectName = action.payload.trim();
			newState.validationErrors = {
				...state.validationErrors,
				projectName: validateProjectName(projectName, state),
			};

			return newState;
		}
		case PROJECT_KEY_CHANGE: {
			const newState = {
				...state,
				projectKey: action.payload,
			};

			const projectKey = action.payload.trim();
			newState.validationErrors = {
				...state.validationErrors,
				projectKey: validateProjectKey(projectKey, state),
			};

			return newState;
		}
		case RELEASE_PROJECT_KEY: {
			const { key, isReleased } = action.payload;

			const nextReleasedProjectKeys = new Set(
				isReleased
					? state.releasedProjectKeys.concat(key)
					: state.releasedProjectKeys.filter((k) => k !== key && k !== ''),
			);

			return {
				...state,
				releasedProjectKeys: [...nextReleasedProjectKeys],
			};
		}
		case PROJECT_DESCRIPTION_CHANGE: {
			return {
				...state,
				projectDescription: action.payload,
			};
		}
		case PROJECT_URL_CHANGE: {
			const newState = {
				...state,
				projectUrl: action.payload,
			};

			const projectUrl = action.payload.trim();
			newState.validationErrors = {
				...state.validationErrors,
				projectUrl: validateProjectUrl(projectUrl),
			};

			return newState;
		}
		case PROJECT_TEAM_TYPE_CHANGE:
			return {
				...state,
				projectTeamType: action.payload,
			};
		case PROJECT_TEAM_TYPE_FREE_TEXT_CHANGE: {
			const newState = {
				...state,
				projectTeamTypeFreeText: action.payload,
				error: action.error,
			};
			newState.validationErrors = {
				...state.validationErrors,
				projectTeamTypeFreeText: action.error
					? {
							type: 'error',
							key: PROJECT_TEAM_TYPE_FREE_TEXT_ERROR,
						}
					: undefined,
			};
			return newState;
		}
		case ASSIGNEE_TYPE_CHANGE: {
			return {
				...state,
				assigneeType: action.payload,
			};
		}
		case DELETE_PROJECT: {
			return {
				...state,
				isDeleting: true,
			};
		}
		case DELETE_PROJECT_SUCCESS: {
			return {
				...state,
				isDeleting: false,
				isDeleteProjectModalOpen: false,
				flags: state.flags.concat(makeFlag(DELETE_PROJECT_SUCCESS_FLAG)),
			};
		}
		case DELETE_PROJECT_FAIL: {
			return {
				...state,
				isDeleting: false,
				isDeleteProjectModalOpen: false,
				flags: state.flags.concat(makeFlag(DELETE_PROJECT_FAIL_FLAG)),
			};
		}
		case UPLOAD_AVATAR: {
			return {
				...state,
				isUploadingAvatar: true,
				isAvatarPickerDialogOpen: false,
			};
		}
		case OPEN_DELETE_PROJECT_MODAL: {
			return {
				...state,
				isDeleteProjectModalOpen: true,
			};
		}
		case CLOSE_DELETE_PROJECT_MODAL: {
			return {
				...state,
				isDeleteProjectModalOpen: false,
			};
		}

		case PRELOAD_AVATAR_IMAGE_SUCCESS: {
			return {
				...state,
				isAvatarImageLoaded: true,
			};
		}
		case SET_PROJECT_CATEGORY_ID:
			return {
				...state,
				projectCategoryId: action.payload,
			};
		case SET_PROJECT_LEAD_ACCOUNT_ID:
			return {
				...state,
				projectLeadAccountId: action.payload,
			};
		case OPEN_TRASH_PROJECT_MODAL:
			return {
				...state,
				isTrashProjectModalOpen: true,
			};
		case CLOSE_TRASH_PROJECT_MODAL:
		case TRASH_PROJECT_SUCCESS:
		case TRASH_PROJECT_FAIL:
			return {
				...state,
				isTrashProjectModalOpen: false,
			};
		case ARCHIVE_PROJECT_SUCCESS: {
			return {
				...state,
			};
		}
		case OPEN_ARCHIVE_PROJECT_MODAL:
			return {
				...state,
				isArchiveProjectModalOpen: true,
			};
		case CLOSE_ARCHIVE_PROJECT_MODAL:
			return {
				...state,
				isArchiveProjectModalOpen: false,
			};
		case FIELD_VALIDATION_FORCED:
			return {
				...state,
				forceValidationFor: action.payload,
			};
		case UNDO_RELEASE_HISTORICAL_KEYS_CLICKED:
			return {
				...state,
				releasedProjectKeys: [],
			};
		case SAVE_BUTTON_CLICKED:
			return state;
		case SET_DROP_DOWN_MENU_BUTTON_REF:
			return {
				...state,
				dropDownMenuButtonRef: action.payload,
			};
		default: {
			const _exhaustiveCheck: never = action;

			return state;
		}
	}
};
